/**
 * Local player identity, persisted to localStorage. The `clientId` is a stable
 * id mirrored into the `join` intent so the server can re-attach a returning
 * player to their persisted balance (state.ts `PersistedPlayer.id`).
 */

import type { Role } from '@blackjack/shared';
import type { Identity } from '../state/store';

const STORAGE_KEY = 'blackjack.identity.v1';

/** Generate a stable, reasonably-unique client id. */
function makeClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Read the persisted identity (or undefined if none / corrupt). */
export function loadIdentity(): Identity | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<Identity>;
    if (
      typeof parsed.clientId === 'string' &&
      typeof parsed.name === 'string' &&
      (parsed.role === 'player' || parsed.role === 'croupier')
    ) {
      return { clientId: parsed.clientId, name: parsed.name, role: parsed.role };
    }
  } catch {
    /* ignore corrupt storage */
  }
  return undefined;
}

/** Persist (and return) an identity, minting a clientId if there isn't one yet. */
export function saveIdentity(name: string, role: Role): Identity {
  const existing = loadIdentity();
  const clientId = existing?.clientId ?? makeClientId();
  const identity: Identity = { clientId, name: name.trim().slice(0, 24), role };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    /* storage may be unavailable (private mode); identity still works in-memory */
  }
  return identity;
}

/** Peek/mint just the stable clientId without committing a name/role. */
export function ensureClientId(): string {
  const existing = loadIdentity();
  return existing?.clientId ?? makeClientId();
}
